import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { showSuccessToast } from '@/utils/toast'
import { useCustomerStore } from '@/stores/CustomersStore'

// Tag types used by the backend: 'events' (visit tags) | 'clients' (customer tags)
export const useTagsStore = defineStore('tagsStore', () => {
  // ========================================
  // Selectable tags (cached per type)
  // ========================================
  const availableTags = reactive({ events: null, clients: null }) // null = not fetched yet
  const canCreate = reactive({ events: null, clients: null }) // null = unknown until fetched
  const isLoading = reactive({ events: false, clients: false })

  // Fetches selectable tags once per type; later calls reuse the cached list
  const fetchTags = async type => {
    if (availableTags[type] || isLoading[type]) return
    isLoading[type] = true

    try {
      const response = await $backend('/tags/index', {
        params: { type },
        method: 'GET',
      })

      if (response.success) {
        availableTags[type] = response.data.tags
        canCreate[type] = response.data.canCreate
      }
    } finally {
      isLoading[type] = false
    }
  }

  const clearCache = () => {
    availableTags.events = null
    availableTags.clients = null
  }

  // ========================================
  // Management (list / save / delete)
  // ========================================

  // Last saved/deleted tag; TagSelectors watch it to refresh their selected chips
  const lastChange = ref(null) // { action: 'created' | 'saved' | 'deleted', tag }

  const replaceInList = (list, tag) => list?.map(item => item.id === tag.id ? { ...item, name: tag.name, color: tag.color, search: tag.search } : item)

  // Keeps tags of the selected customer (shown in visit dialog) in sync
  const applyChange = (action, tag) => {
    const customerStore = useCustomerStore()
    const customer = customerStore.selectedCustomer

    if (customer?.tags) {
      customer.tags = action === 'deleted'
        ? customer.tags.filter(item => item.id !== tag.id)
        : replaceInList(customer.tags, tag)
    }
    clearCache()
    lastChange.value = { action, tag }
  }

  /**
   * Paginated list of all tags
   * @param {{ page: number, perPage: number, search?: string, type?: string, column?: 'name'|'create_time', direction?: 'asc'|'desc' }} options
   */
  const listTags = async ({ page, perPage, search = '', type = null, column = 'create_time', direction = 'desc' }) => {
    const params = {
      page,
      perPage,
      'orders[0][column]': column,
      'orders[0][direction]': direction,
    }

    if (search) params.search = search
    if (type) params.type = type

    return await $backend('/tags/list', { params, method: 'GET' })
  }

  // Creates (id: null) or updates a tag; returns the saved tag or null
  const saveTag = async ({ id = null, name, color, forEvents, forClients }) => {
    const response = await $backend('/tags/save', {
      method: 'POST',
      body: {
        id,
        name,
        color,
        forEvents,
        forClients,

        // ponytail: backend's confirm flow not handled yet, always false
        confirmed: false,
      },
    })

    if (!response.success) return null
    showSuccessToast(response.message)
    applyChange(id ? 'saved' : 'created', response.data.tag)

    return response.data.tag
  }

  const deleteTag = async tag => {
    const response = await $backend('/tags/delete', {
      method: 'POST',
      body: { id: tag.id },
    })

    if (!response.success) return false
    showSuccessToast(response.message)
    applyChange('deleted', tag)

    return true
  }

  return {
    availableTags,
    canCreate,
    isLoading,
    fetchTags,
    lastChange,
    listTags,
    saveTag,
    deleteTag,
  }
})
